import { platformSections } from "./platformDocsData";

export interface OverviewSection {
  id: string;
  title: string;
  group: string;
  content: string;
}

// Workflow automation sections
const workflowSections: OverviewSection[] = [
  {
    id: "what-is-workflow",
    title: "What is a Workflow?",
    group: "Workflow Automation",
    content: `
      <p>A workflow is a visual automation pipeline built by connecting <strong>nodes</strong> on a canvas. Each node performs a single operation — receiving a file, extracting text, transforming data, making a decision, or exporting results. You wire them together to create end-to-end document processing without writing code.</p>

      <div class="wf-callout wf-callout-info">
        Think of it as a flowchart that actually runs. Every connection between nodes is a real data pipe — when one node finishes, its output flows into the next.
      </div>

      <h3>Core principles</h3>
      <ul class="wf-list">
        <li><strong>Visual-first</strong> — drag nodes onto a canvas, draw connections, configure parameters. No scripting required.</li>
        <li><strong>Modular</strong> — every node is self-contained. Swap, rearrange, or duplicate freely.</li>
        <li><strong>Event-driven</strong> — workflows start when a trigger fires (file upload, email arrival, API call) and run automatically.</li>
        <li><strong>Data flows forward</strong> — each node receives input from its predecessor and passes output to its successor.</li>
      </ul>
    `,
  },
  {
    id: "workflow-lifecycle",
    title: "Workflow Lifecycle",
    group: "Workflow Automation",
    content: `
      <p>Every workflow follows the same three-phase pattern: <strong>Trigger</strong>, <strong>Process</strong>, <strong>Export</strong>.</p>

      <div class="wf-pipeline-visual">
        <div class="wf-pipeline-node wf-pipeline-trigger">Trigger</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-action">Process (1 or more nodes)</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-export">Export</div>
      </div>

      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Trigger</p>
            <p class="wf-step-desc">An event starts the workflow — a file is uploaded, an email arrives, an API call hits the endpoint, or a cloud folder changes. Every workflow begins with exactly one trigger node.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Process</p>
            <p class="wf-step-desc">Data flows through action nodes (OCR, transform, AI extraction), conditional nodes (if-else branching, switch routing, loops), and any combination you need. This is where the actual work happens.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Export</p>
            <p class="wf-step-desc">Processed data is delivered to its final destination — a database, a webhook endpoint, a generated document, or cloud storage.</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: "execution-patterns",
    title: "Execution Patterns",
    group: "Workflow Automation",
    content: `
      <p>Nodes can be arranged in three execution patterns depending on how your data needs to flow.</p>

      <div class="wf-patterns">
        <div class="wf-pattern">
          <div class="wf-pattern-label">Sequential</div>
          <div class="wf-pattern-flow">
            <span>A</span><span class="wf-pattern-arrow">&rarr;</span><span>B</span><span class="wf-pattern-arrow">&rarr;</span><span>C</span>
          </div>
          <p>Each node waits for the previous node to finish before executing. This is the default and most common pattern.</p>
        </div>
        <div class="wf-pattern">
          <div class="wf-pattern-label">Branching</div>
          <div class="wf-pattern-flow">
            <span>If-Else</span><span class="wf-pattern-arrow">&rarr;</span><span>Path A &thinsp;|&thinsp; Path B</span>
          </div>
          <p>Conditional nodes split the flow into separate paths. Only the matching branch executes.</p>
        </div>
        <div class="wf-pattern">
          <div class="wf-pattern-label">Iterative</div>
          <div class="wf-pattern-flow">
            <span>For Each</span><span class="wf-pattern-arrow">&rarr;</span><span>Process item</span><span class="wf-pattern-arrow">&#8634;</span><span>Done</span>
          </div>
          <p>The For Each node loops over a list, running downstream nodes once per item, then continues.</p>
        </div>
      </div>
    `,
  },
  {
    id: "node-categories",
    title: "Available Nodes",
    group: "Workflow Automation",
    content: `
      <p>DocIQ provides <strong>24 nodes</strong> across four categories. Here is every node available in the workflow builder.</p>

      <div class="wf-node-cats">
        <div class="wf-node-cat">
          <div class="wf-node-cat-header" style="border-left-color: #ef4444;">Triggers</div>
          <p>Start a workflow when an event occurs. Every workflow needs exactly one trigger.</p>
          <table>
            <thead><tr><th>Node</th><th>What it does</th></tr></thead>
            <tbody>
              <tr><td><strong>On Document Upload</strong></td><td>Fires when a user uploads a file through the DocIQ interface</td></tr>
              <tr><td><strong>DocIQ Trigger</strong></td><td>Fires when a document is added to a pre-configured document store</td></tr>
              <tr><td><strong>HTTP Trigger</strong></td><td>Exposes a REST endpoint — fires when an external system sends a request</td></tr>
              <tr><td><strong>OneDrive Trigger</strong></td><td>Polls a OneDrive folder and fires when new or updated files are detected</td></tr>
              <tr><td><strong>Gmail Trigger</strong></td><td>Monitors a Gmail inbox for new emails matching your filters</td></tr>
              <tr><td><strong>Outlook Trigger</strong></td><td>Monitors an Outlook inbox for new emails matching your filters</td></tr>
            </tbody>
          </table>
        </div>

        <div class="wf-node-cat">
          <div class="wf-node-cat-header" style="border-left-color: #f59e0b;">Actions</div>
          <p>Process, transform, and enrich data. The heavy-lifting nodes of any workflow.</p>
          <table>
            <thead><tr><th>Node</th><th>What it does</th></tr></thead>
            <tbody>
              <tr><td><strong>OCR Extraction</strong></td><td>Extract text and structured data from scanned documents and images</td></tr>
              <tr><td><strong>Cloud OCR</strong></td><td>Google Cloud Vision powered OCR for high-accuracy text extraction</td></tr>
              <tr><td><strong>Smart Extraction (Gemini)</strong></td><td>AI-powered structured data extraction using a JSON schema you define</td></tr>
              <tr><td><strong>LLM Node</strong></td><td>Run AI prompts for summarization, classification, or free-form processing</td></tr>
              <tr><td><strong>Email Parsing</strong></td><td>Extract structured fields from email body, headers, and attachments</td></tr>
              <tr><td><strong>Compare Data</strong></td><td>Diff two data sets and output a similarity score with field-level differences</td></tr>
              <tr><td><strong>Transform</strong></td><td>Map, rename, and restructure data fields between formats</td></tr>
              <tr><td><strong>Code Node</strong></td><td>Execute custom JavaScript for logic that no built-in node covers</td></tr>
              <tr><td><strong>HTTP Request</strong></td><td>Call any external REST API mid-workflow and use the response downstream</td></tr>
              <tr><td><strong>PDF Generator</strong></td><td>Generate formatted PDF documents from workflow data using templates</td></tr>
            </tbody>
          </table>
        </div>

        <div class="wf-node-cat">
          <div class="wf-node-cat-header" style="border-left-color: #8b5cf6;">Conditionals</div>
          <p>Control flow direction with branching, routing, and iteration.</p>
          <table>
            <thead><tr><th>Node</th><th>What it does</th></tr></thead>
            <tbody>
              <tr><td><strong>If-Else</strong></td><td>Evaluate conditions and route data to a True or False branch</td></tr>
              <tr><td><strong>Switch</strong></td><td>Route data to one of many output paths based on a variable's value</td></tr>
              <tr><td><strong>For Each</strong></td><td>Loop over an array, executing downstream nodes once per item</td></tr>
            </tbody>
          </table>
        </div>

        <div class="wf-node-cat">
          <div class="wf-node-cat-header" style="border-left-color: #10b981;">Exports</div>
          <p>Deliver processed data to its final destination.</p>
          <table>
            <thead><tr><th>Node</th><th>What it does</th></tr></thead>
            <tbody>
              <tr><td><strong>MongoDB Connector</strong></td><td>Insert or update documents in a MongoDB collection</td></tr>
              <tr><td><strong>Webhook Export</strong></td><td>POST processed data to any external URL endpoint</td></tr>
              <tr><td><strong>Export to Excel</strong></td><td>Generate .xlsx spreadsheets from structured workflow data</td></tr>
              <tr><td><strong>Export to Word</strong></td><td>Generate .docx documents using templates and workflow data</td></tr>
              <tr><td><strong>OneDrive Upload</strong></td><td>Upload generated files or processed documents to OneDrive</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    `,
  },
  {
    id: "building-workflow",
    title: "Building a Workflow",
    group: "Workflow Automation",
    content: `
      <p>Follow these steps to create and run a workflow from scratch.</p>

      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Create a new workflow</p>
            <p class="wf-step-desc">Open the workflow builder. You'll see a blank canvas with the node palette on the left.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Add a trigger</p>
            <p class="wf-step-desc">Drag a trigger node onto the canvas. This determines <em>how</em> your workflow starts — file upload, email, API call, or cloud folder monitoring.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Add processing nodes</p>
            <p class="wf-step-desc">Drag action nodes (OCR, Transform, LLM, etc.) and connect them. Each connection carries data from one node to the next.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">4</div>
          <div>
            <p class="wf-step-title">Add conditions (optional)</p>
            <p class="wf-step-desc">Use If-Else or Switch nodes to route data down different paths based on values, thresholds, or field existence.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">5</div>
          <div>
            <p class="wf-step-title">Add export nodes</p>
            <p class="wf-step-desc">Finish by adding one or more export nodes — save to MongoDB, send a webhook, generate a document, or upload to OneDrive.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">6</div>
          <div>
            <p class="wf-step-title">Configure each node</p>
            <p class="wf-step-desc">Click any node to open its settings panel. Map input fields, set parameters, and reference outputs from previous nodes.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Save and activate</p>
            <p class="wf-step-desc">Save your workflow and activate it. It will now run automatically whenever its trigger fires.</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: "common-patterns",
    title: "Common Workflow Patterns",
    group: "Workflow Automation",
    content: `
      <p>These are the most frequently used workflow patterns. Each can be built in minutes using the node palette.</p>

      <div class="wf-use-cases">
        <div class="wf-use-case">
          <p class="wf-use-case-title">Invoice Processing</p>
          <p class="wf-use-case-flow">On Document Upload &rarr; OCR Extraction &rarr; Smart Extraction (Gemini) &rarr; If-Else &rarr; MongoDB &rarr; Webhook</p>
          <p>Upload an invoice, extract text via OCR, pull structured fields (vendor, amount, date) with Gemini, validate with If-Else, store in database, and notify via webhook.</p>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">Email Attachment Processing</p>
          <p class="wf-use-case-flow">Gmail Trigger &rarr; Email Parsing &rarr; For Each &rarr; OCR Extraction &rarr; Transform &rarr; Export to Excel</p>
          <p>Monitor an inbox for incoming emails, extract attachments, loop through each file, run OCR, transform the data, and compile results into a spreadsheet.</p>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">Document Comparison & Reporting</p>
          <p class="wf-use-case-flow">HTTP Trigger &rarr; Compare Data &rarr; LLM Node &rarr; PDF Generator &rarr; OneDrive Upload</p>
          <p>Receive two document versions via API, diff them with Compare Data, generate a human-readable summary with the LLM node, produce a PDF report, and save to OneDrive.</p>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">Multi-Format Document Routing</p>
          <p class="wf-use-case-flow">OneDrive Trigger &rarr; Cloud OCR &rarr; Switch &rarr; Transform (per type) &rarr; MongoDB</p>
          <p>Watch a OneDrive folder for new files, extract content, use a Switch node to route by document type (invoice, receipt, contract), apply type-specific transforms, and store each separately.</p>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">Custom Logic Pipeline</p>
          <p class="wf-use-case-flow">DocIQ Trigger &rarr; Code Node &rarr; HTTP Request &rarr; Transform &rarr; Export to Word</p>
          <p>Trigger from the document store, run custom JavaScript validation, call an external pricing API, reshape the response, and generate a formatted Word document.</p>
        </div>
      </div>
    `,
  },
  {
    id: "e2e-example",
    title: "End-to-End Example",
    group: "Workflow Automation",
    content: `
      <p>Here is a complete invoice processing workflow showing how nodes connect in practice.</p>

      <div class="wf-pipeline-visual">
        <div class="wf-pipeline-node wf-pipeline-trigger">On Document Upload</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-action">OCR Extraction</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-action">Smart Extraction (Gemini)</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-condition">If-Else: Amount &gt; $5,000?</div>
        <div class="wf-pipeline-branch">
          <div class="wf-pipeline-branch-yes">
            <span class="wf-pipeline-branch-label">Yes</span>
            <div class="wf-pipeline-node wf-pipeline-action">LLM Node: Flag for Review</div>
            <div class="wf-pipeline-arrow">&darr;</div>
            <div class="wf-pipeline-node wf-pipeline-export">Webhook: Notify Approver</div>
          </div>
          <div class="wf-pipeline-branch-no">
            <span class="wf-pipeline-branch-label">No</span>
            <div class="wf-pipeline-node wf-pipeline-action">Transform: Format for DB</div>
          </div>
        </div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-export">MongoDB Connector</div>
        <div class="wf-pipeline-arrow">&darr;</div>
        <div class="wf-pipeline-node wf-pipeline-export">Export to Excel</div>
      </div>

      <h3>Step breakdown</h3>
      <table>
        <thead><tr><th>Step</th><th>Node</th><th>What happens</th></tr></thead>
        <tbody>
          <tr><td>1</td><td><strong>On Document Upload</strong></td><td>User uploads an invoice PDF. The file binary and metadata enter the workflow.</td></tr>
          <tr><td>2</td><td><strong>OCR Extraction</strong></td><td>Scanned image is converted to machine-readable text.</td></tr>
          <tr><td>3</td><td><strong>Smart Extraction</strong></td><td>Gemini extracts structured fields — vendor name, invoice number, line items, total amount.</td></tr>
          <tr><td>4</td><td><strong>If-Else</strong></td><td>Checks if <code>total_amount &gt; 5000</code>. Routes to the appropriate branch.</td></tr>
          <tr><td>5a</td><td><strong>LLM Node</strong></td><td>High-value invoices get an AI-generated review summary with risk flags.</td></tr>
          <tr><td>5b</td><td><strong>Transform</strong></td><td>Low-value invoices are reformatted directly for database insertion.</td></tr>
          <tr><td>6</td><td><strong>MongoDB Connector</strong></td><td>Both paths converge — processed invoice data is stored in the database.</td></tr>
          <tr><td>7</td><td><strong>Export to Excel</strong></td><td>A running Excel report is updated with the new invoice entry.</td></tr>
        </tbody>
      </table>
    `,
  },
  {
    id: "data-flow",
    title: "How Data Flows",
    group: "Workflow Automation",
    content: `
      <p>Understanding data flow is key to building effective workflows. Each node receives an input object, processes it, and outputs a result that becomes the input for the next node.</p>

      <h3>Input and output</h3>
      <p>Every node has:</p>
      <ul class="wf-list">
        <li><strong>Input handle</strong> (left side) — receives data from the previous node's output</li>
        <li><strong>Output handle</strong> (right side) — sends processed data to the next node</li>
      </ul>
      <p>Conditional nodes have multiple output handles — one per branch (True/False for If-Else, numbered cases for Switch, Loop/Done for For Each).</p>

      <h3>Referencing previous outputs</h3>
      <p>When configuring a node, you can reference fields from any earlier node's output. For example, if an OCR node outputs <code>extracted_text</code>, the next Transform node can map that field to a new structure.</p>

      <div class="wf-callout wf-callout-info">
        Data is passed as JSON objects between nodes. Each node's configuration panel shows which input fields are available from upstream nodes.
      </div>

      <h3>Binary data</h3>
      <p>File-based nodes (triggers, OCR, PDF Generator, OneDrive Upload) pass binary data alongside the JSON payload. Binary data includes the file buffer, MIME type, and filename. Downstream nodes that need the original file can access it through the binary field.</p>
    `,
  },
  {
    id: "best-practices",
    title: "Best Practices",
    group: "Workflow Automation",
    content: `
      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Tips for building reliable workflows</p>
        <ul>
          <li>Start simple — get a basic trigger-to-export pipeline working before adding complexity.</li>
          <li>Test each node individually before connecting the full chain.</li>
          <li>Name your workflows clearly so they're easy to identify in the workflow list.</li>
        </ul>
      </div>

      <h3>Node selection</h3>
      <table>
        <thead><tr><th>If you need to...</th><th>Use this node</th></tr></thead>
        <tbody>
          <tr><td>Extract text from a scanned PDF</td><td><strong>OCR Extraction</strong> or <strong>Cloud OCR</strong></td></tr>
          <tr><td>Pull specific fields (name, date, amount) from a document</td><td><strong>Smart Extraction (Gemini)</strong></td></tr>
          <tr><td>Summarize or classify a document</td><td><strong>LLM Node</strong></td></tr>
          <tr><td>Process email content and attachments</td><td><strong>Email Parsing</strong></td></tr>
          <tr><td>Reshape data into a different format</td><td><strong>Transform</strong></td></tr>
          <tr><td>Run custom validation or calculation</td><td><strong>Code Node</strong></td></tr>
          <tr><td>Call an external API</td><td><strong>HTTP Request</strong></td></tr>
          <tr><td>Route based on a single condition</td><td><strong>If-Else</strong></td></tr>
          <tr><td>Route based on multiple possible values</td><td><strong>Switch</strong></td></tr>
          <tr><td>Process each item in a list</td><td><strong>For Each</strong></td></tr>
          <tr><td>Compare two versions of a document</td><td><strong>Compare Data</strong></td></tr>
          <tr><td>Generate a PDF report</td><td><strong>PDF Generator</strong></td></tr>
        </tbody>
      </table>

      <h3>Conditional logic</h3>
      <ul class="wf-list">
        <li>Use <strong>If-Else</strong> for simple true/false decisions (e.g., "is confidence score above 0.85?").</li>
        <li>Use <strong>Switch</strong> when routing by a value with 3+ possible outcomes (e.g., document type: invoice, receipt, contract, other).</li>
        <li>Use <strong>For Each</strong> when dealing with arrays — extracted line items, multiple attachments, batch files.</li>
        <li>Chain multiple conditionals for complex decision trees.</li>
      </ul>

      <h3>Error handling</h3>
      <ul class="wf-list">
        <li>Always connect the <strong>Else / fallback</strong> output of Switch nodes to handle unexpected values.</li>
        <li>Use <strong>If-Else</strong> after OCR nodes to check if extraction was successful before processing further.</li>
        <li>Add a <strong>Webhook Export</strong> at error branches to notify your team when something fails.</li>
      </ul>
    `,
  },
  {
    id: "integrations-overview",
    title: "Workflow Integrations",
    group: "Workflow Automation",
    content: `
      <p>DocIQ workflows connect to external systems through trigger and export nodes. Here is the full integration map.</p>

      <table>
        <thead>
          <tr><th>Direction</th><th>Service</th><th>Node</th><th>How it connects</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Inbound</strong></td><td>Microsoft OneDrive</td><td>OneDrive Trigger</td><td>Polls a folder via OAuth for new/updated files</td></tr>
          <tr><td><strong>Inbound</strong></td><td>Google Gmail</td><td>Gmail Trigger</td><td>OAuth connection monitors inbox with label/subject filters</td></tr>
          <tr><td><strong>Inbound</strong></td><td>Microsoft Outlook</td><td>Outlook Trigger</td><td>OAuth connection monitors inbox with folder/subject filters</td></tr>
          <tr><td><strong>Inbound</strong></td><td>Any REST client</td><td>HTTP Trigger</td><td>Exposes a unique URL endpoint that accepts POST/GET requests</td></tr>
          <tr><td><strong>Inbound</strong></td><td>DocIQ UI</td><td>On Document Upload</td><td>Direct file upload through the DocIQ application</td></tr>
          <tr><td><strong>Outbound</strong></td><td>MongoDB</td><td>MongoDB Connector</td><td>Insert or upsert documents into any collection</td></tr>
          <tr><td><strong>Outbound</strong></td><td>Any REST endpoint</td><td>Webhook Export</td><td>POST JSON payload to a configured URL</td></tr>
          <tr><td><strong>Outbound</strong></td><td>Any REST API</td><td>HTTP Request</td><td>Call external APIs with configurable method, headers, and body</td></tr>
          <tr><td><strong>Outbound</strong></td><td>Microsoft OneDrive</td><td>OneDrive Upload</td><td>Upload files to a specific OneDrive folder via OAuth</td></tr>
          <tr><td><strong>Outbound</strong></td><td>Excel (.xlsx)</td><td>Export to Excel</td><td>Generate downloadable spreadsheets from workflow data</td></tr>
          <tr><td><strong>Outbound</strong></td><td>Word (.docx)</td><td>Export to Word</td><td>Generate formatted documents using templates</td></tr>
          <tr><td><strong>AI</strong></td><td>Google Cloud Vision</td><td>Cloud OCR</td><td>High-accuracy OCR via Google Cloud Vision API</td></tr>
          <tr><td><strong>AI</strong></td><td>Google Gemini</td><td>Smart Extraction</td><td>Structured field extraction using Gemini with JSON schema</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">OAuth setup</p>
        <ul>
          <li>OneDrive, Gmail, and Outlook nodes require a one-time OAuth authorization from the node's settings panel.</li>
          <li>Once connected, the token refreshes automatically — no re-authentication needed.</li>
          <li>Each workflow can use different OAuth accounts if needed.</li>
        </ul>
      </div>
    `,
  },
];

// Combine platform sections (from platformDocsData.ts) and workflow sections
// Order: Getting Started > Document Management > Workflow Automation > User Management > Reports & Analytics > Settings
export const overviewSections: OverviewSection[] = [
  ...platformSections.filter((s) => s.group === "Getting Started"),
  ...workflowSections,
  ...platformSections.filter((s) => s.group === "User Management"),
  ...platformSections.filter((s) => s.group === "Reports & Analytics"),
  ...platformSections.filter((s) => s.group === "Settings"),
];

// Export the ordered list of groups for sidebar rendering
export const overviewGroups: string[] = [
  "Getting Started",
  "Workflow Automation",
  "User Management",
  "Reports & Analytics",
  "Settings",
];

// ── Documents tab: document features + the connectors that bring data in ──
const documentImportSections: OverviewSection[] = [
  {
    id: "web-scraping",
    title: "Web Scraping",
    group: "Import & Connectors",
    content: `
      <p>Crawl a website and pull its pages (and linked files) straight into a document store. Start from a folder's <strong>Web Scraper</strong> action, give it a seed URL, and the crawler captures content so it becomes searchable in DocxIQ.</p>
      <h3>What you can configure</h3>
      <ul class="wf-list">
        <li><strong>Seed URL</strong> — the page the crawl starts from.</li>
        <li><strong>Allowed file types</strong> — restrict which linked files are downloaded (e.g. PDF, DOCX).</li>
        <li><strong>Respect robots.txt</strong> — honor the site's crawl rules (recommended).</li>
        <li><strong>Crawl subdomains</strong> — follow links into subdomains of the seed host.</li>
        <li><strong>Change detection</strong> — re-crawl only pages that changed since last time.</li>
      </ul>
      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Tip</p>
        <p>Pair scraping with a schedule so a site stays continuously synced into your store.</p>
      </div>
    `,
  },
  {
    id: "spreadsheet-connector",
    title: "Spreadsheet Connector",
    group: "Import & Connectors",
    content: `
      <p>Connect a Google Sheet to a folder and keep its rows flowing into DocxIQ. Authorize once with OAuth, pick the sheet, and the connector syncs the data on demand or on a schedule.</p>
      <h3>How it works</h3>
      <ul class="wf-list">
        <li><strong>Authorize</strong> — sign in with Google to grant read access.</li>
        <li><strong>Select source</strong> — choose the spreadsheet and worksheet to sync.</li>
        <li><strong>Sync</strong> — rows are imported into the folder for processing/search.</li>
      </ul>
    `,
  },
  {
    id: "external-connections",
    title: "External Connections (API & Database)",
    group: "Import & Connectors",
    content: `
      <p>Connect a folder to an external <strong>API endpoint</strong> or <strong>database</strong> (MongoDB or PostgreSQL) so documents and data are pulled in automatically. Manage everything from the folder's <strong>Connections</strong> page — Connected sources, API endpoints, and Databases.</p>
      <h3>Capabilities</h3>
      <table>
        <thead><tr><th>Feature</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>API endpoints</strong></td><td>Pull files or JSON data from a REST API, with pagination and POST-body support.</td></tr>
          <tr><td><strong>Databases</strong></td><td>Import from MongoDB or PostgreSQL — as files, or by serializing rows to documents.</td></tr>
          <tr><td><strong>Auto-sync</strong></td><td>Schedule a connection to refresh on a cron interval.</td></tr>
          <tr><td><strong>Incremental sync</strong></td><td>Only new or changed records are imported each run — unchanged data is skipped, so no wasted AI processing.</td></tr>
          <tr><td><strong>Active / inactive</strong></td><td>Pause a connection to stop unwanted auto-syncs.</td></tr>
          <tr><td><strong>Sync logs</strong></td><td>Every run is logged with imported/skipped counts, file names, duration, and trigger type.</td></tr>
        </tbody>
      </table>
      <div class="wf-callout wf-callout-info">
        A folder can have multiple connections at once (several APIs and databases), each with its own schedule.
      </div>
    `,
  },
];

export const documentSections: OverviewSection[] = [
  ...platformSections.filter((s) => s.group === "Document Management"),
  ...documentImportSections,
];

export const documentGroups: string[] = [
  "Document Management",
  "Import & Connectors",
];
